import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { IRecommendations, RecommendationType, SaveTriggerPayload } from '../../models/consultation-recommendation.model';
import { TasksService } from '../tasks/tasks.service';
import { MedicationService } from '../medication/medication.service';
import { ClassificationService } from '../classification/classification.service';
import { AccEligibilityService } from '../acc-eligibility/acc-eligibility.service';

@Injectable({
  providedIn: 'root'
})
export class ConsultationRecommendationService {
  private readonly drawerTrigger$ = new Subject<RecommendationType>();
  drawerTriggerObservable$ = this.drawerTrigger$.asObservable();

  private readonly generateNotesTriggerSubject$ = new Subject<SaveTriggerPayload>();
  generateNotesTrigger$ = this.generateNotesTriggerSubject$.asObservable();

  generateNotesReady$ = new BehaviorSubject<boolean>(false);
  generateNotesFailed$ = new BehaviorSubject<boolean>(false);

  lastTranscriptionHash: string | null = null;
  lastPatientId: string | null = null;

  constructor(
    private readonly tasksService: TasksService,
    private readonly medicationService: MedicationService,
    private readonly classificationService: ClassificationService,
    private readonly accEligibilityService: AccEligibilityService
  ) { }

  openDrawer(type: RecommendationType): void {
    this.drawerTrigger$.next(type);
  }

  setRecommendations(payload: IRecommendations) {
    if (payload.tasks) {
      this.tasksService.setTasks(JSON.parse(JSON.stringify(payload.tasks)));
    }
    if (payload.prescriptions) {
      this.medicationService.setMedications(JSON.parse(JSON.stringify(payload.prescriptions)));
    }
    if (payload.classification) {
      this.classificationService.setClassifications(JSON.parse(JSON.stringify(payload.classification)))
    }
    if (payload.accEligibility) {
      this.accEligibilityService.setAccEligibility(JSON.parse(JSON.stringify(payload.accEligibility)))
    }

  }

  getRecommendations(): IRecommendations {
    return JSON.parse(JSON.stringify({
      tasks: this.tasksService.getTasks(),
      prescriptions: this.medicationService.getMedications(),
      classification: this.classificationService.getClassifications(),
      accEligibility: this.accEligibilityService.getAccEligibility()
    }))
  }

  clearAllRecommendations() {
    this.tasksService.clearAll();
    this.medicationService.clearAll();
    this.classificationService.clearAll();
    this.accEligibilityService.clearAll();
    this.lastTranscriptionHash = null;
    this.lastPatientId = null;
  }

  triggerGenerateNote(payload: SaveTriggerPayload) {
    this.generateNotesReady$.next(false);
    this.generateNotesFailed$.next(false);

    const patientId = this.getPatientId();
    this.hasTranscriptionChanged(
      patientId,
      payload.transcript
    )
      .then((isTranscriptionChanged: boolean) => {
        payload.isTranscriptionChanged = isTranscriptionChanged;
        this.generateNotesTriggerSubject$.next(payload);
      })
  }

  async hashTranscription(conversation: string): Promise<string> {
    const normalized = conversation.trim().replace(/\s+/g, ' ');
    const data = new TextEncoder().encode(normalized);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  async hasTranscriptionChanged(
    patientId: string,
    conversation: string
  ): Promise<boolean> {
    const hash = await this.hashTranscription(conversation);

    if (
      patientId === this.lastPatientId &&
      hash === this.lastTranscriptionHash) {
      return false;
    }

    this.lastPatientId = patientId;
    this.lastTranscriptionHash = hash;
    return true;
  }


  getPatientId(): string {
    let patientId = '';
    try {
      const appointmentInfo = JSON.parse(localStorage.getItem('currentAppointment') || '{}');
      patientId = appointmentInfo?.patient?.referenceId || '';
      if (!patientId) {
        patientId = localStorage.getItem('patientId') || '';
      }
    } catch {
      patientId = localStorage.getItem('patientId') || '';
    }
    // Generate a unique ID if no patient ID is available
    return patientId || `unknown-${crypto.randomUUID()}`;
  }
}


